/**
 * The one way the Tool builds DOM. Text always goes in as text nodes, so
 * nothing from the API can ever be parsed as markup.
 */
type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, string | number | boolean | null | undefined | ((e: Event) => void)>;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs | null = null,
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (typeof value === "function") {
        el.addEventListener(key.replace(/^on/, "").toLowerCase(), value as EventListener);
      } else if (key === "class") {
        el.className = String(value);
      } else {
        el.setAttribute(key, value === true ? "" : String(value));
      }
    }
  }
  append(el, children);
  return el;
}

export function append(parent: Node, children: (Child | Child[])[]): void {
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "object" ? child : document.createTextNode(String(child)));
  }
}

/** "Heading" element at a computed level, clamped to h1–h6. */
export function heading(level: number, attrs: Attrs | null, ...children: Child[]): HTMLHeadingElement {
  const n = Math.min(6, Math.max(1, level));
  return h(`h${n}` as "h1", attrs, ...children);
}

/** A stable id for aria wiring, unique within the page. */
let idCounter = 0;
export function uid(prefix: string): string {
  idCounter += 1;
  return `bsc-${prefix}-${idCounter}`;
}
